import { useNotification } from '@ipc/ipc-components';
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';

const apiCallWrapper = (apiCall) => (_, params) => apiCall(params);

export const useApiQuery = (queryKey, queryFn, options, variables) => useQuery({
  queryKey,
  queryFn: apiCallWrapper(queryFn),
  variables,
  config: {
    ...options,
    refetchOnWindowFocus: false,
  },
});

const defaultNotificationProps = {
  notifyOnSuccess: true,
  notifyOnError: true,
  messageTimeoutInSeconds: 5,
};

const getErrorMessage = (error) => (error?.data?.message || error?.response?.data?.message || 'Failed operation');

const defaultnotificationProps = {
  notificateOnSuccess: true,
  notificateOnError: true,
  messageTimeoutInSeconds: 8,
};

export const useApiQueryWithPagination = (
  queryKey,
  queryFn,
  options = {
    page: 1, size: 5, sortField: '', sortDirection: '', requestContext: '',
  },
  variables,
) => {
  const [{
    page, size, totalSize, requestContext,
  }, setPageOptions] = useState({
    page: options.page,
    size: options.size,
    requestContext: options.requestContext,
    totalSize: null,
  });

  const [{ sortField, sortDirection }, setSortedField] = useState({
    sortField: options.sortField,
    sortDirection: options.sortDirection,
  });

  const handlePageChange = (newPage) => {
    setPageOptions((previous) => ({ ...previous, page: newPage }));
  };

  const handlePageSizeChange = (newSize) => {
    setPageOptions((previous) => ({
      ...previous,
      size: newSize,
    }));
  };

  const handleSort = (sortF, sortDir) => {
    setSortedField({ sortField: sortF, sortDirection: sortDir });
  };

  let paginatedQueryKey = queryKey;

  if (typeof queryKey === 'string') {
    paginatedQueryKey = [queryKey, {
      options: {
        page, size, sortField, sortDirection, requestContext,
      },
    }];
  } else if (Array.isArray(queryKey)) {
    paginatedQueryKey = [
      queryKey[0],
      {
        ...queryKey[1],
        options: {
          ...queryKey[1].options, page, size, sortField, sortDirection, requestContext,
        },
      },
    ];
  }

  const res = useQuery({
    keepPreviousData: true,
    queryKey: paginatedQueryKey,
    queryFn: apiCallWrapper(queryFn),
    variables,
    config: {
      ...options,
      refetchOnWindowFocus: false,
    },
    ...options,
  });

  const { error, data: resolvedData } = res;
  const { errorNotification } = useNotification();

  useEffect(() => {
    const { notificateOnError, notificationErrorMessage, messageTimeoutInSeconds } = defaultnotificationProps;

    if (error && notificateOnError) {
      const { data: dataNotifications = {} } = error;
      const { error: detailedError = {} } = dataNotifications;
      const { descriptions = [] } = detailedError;
      errorNotification(
        notificationErrorMessage
        || (dataNotifications && dataNotifications.message
          ? dataNotifications.message
          : descriptions.join('\n') || 'Failed operation'),
        messageTimeoutInSeconds,
      );
    }
  }, [error]);

  useEffect(() => {
    if (resolvedData && resolvedData.totalResultNumber !== totalSize) {
      setPageOptions((previous) => ({ ...previous, totalSize: resolvedData.totalResultNumber }));
    }
  }, [resolvedData]);

  const paginationMeta = {
    currentPage: page,
    pageSize: size,
    requestContext,
    totalSize,
    handlePageChange,
    handlePageSizeChange,
  };

  const sortMeta = {
    sortField,
    sortDirection,
    handleSort,
  };

  return {
    ...res, data: resolvedData, paginationMeta, sortMeta,
  };
};

export const useApiMutation = (mutationFn, options, notificationOptions) => {
  const res = useMutation(mutationFn, options);
  const [, { error, data }] = res;
  const { errorNotification, successNotification } = useNotification();

  const mergedNotificationOptions = {
    ...defaultNotificationProps,
    ...notificationOptions,
  };

  useEffect(() => {
    const { notifyOnError, notificationErrorMessage, messageTimeoutInSeconds } = mergedNotificationOptions;

    if (notifyOnError && error) {
      errorNotification(
        notificationErrorMessage || getErrorMessage(error),
        messageTimeoutInSeconds,
      );
    }
  }, [error]);

  useEffect(() => {
    const { notifyOnSuccess, notificationSuccessMessage, messageTimeoutInSeconds } = mergedNotificationOptions;

    if (notifyOnSuccess && data !== undefined) {
      successNotification(
        notificationSuccessMessage || (data.message || 'Success operation'),
        messageTimeoutInSeconds,
      );
    }
  }, [data]);

  return res;
};
